import { useSelectInterval } from '@/components/week-calendar/hooks/useSelectInterval';
import { TTF } from '@/constants/queryParams';
import useAuth from '@/hooks/useAuth';
import { useSearchParams } from '@/hooks/useSearchParams';
import {
	useCreateCalendarEventMutation,
	useListCalendarEvents,
	useRemoveCalendarEventMutation,
} from '@/queries/calendar-event.queries';
import { useGetCalendarQuery } from '@/queries/calendar.queries';
import uuidService from '@/services/uuid.service';
import { ICalendarEvent } from '@/types/calendar-event/ICalendarEvent';
import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import { Temporal } from 'temporal-polyfill';

export function useCalendarWeekView() {
	const auth = useAuth();
	const timeZone = auth.getCurrentUser().timeZone;
	const params = useSearchParams();
	const fromString = params.get(TTF) || Temporal.Now.plainDateISO().toString();
	const calendarIds = params.get('ids');
	// TODO: In the future we need to allow multiple calendars
	const calendarId = calendarIds?.split(',')[0] as string;
	const calendarQuery = useGetCalendarQuery({ id: calendarId });
	const createEventMutation = useCreateCalendarEventMutation();
	const removeCalendarEventMutation = useRemoveCalendarEventMutation();
	const isMutating = useIsMutating() > 0;
	const isFetching = useIsFetching() > 0;
	const isLoading = isFetching || isMutating;

	const selectInterval = useSelectInterval({
		onRangeSelected(range) {
			return createEventMutation.mutateAsync({
				id: uuidService.v4(),
				calendarId,
				interval: {
					inclusiveStart: {
						dateTime: range.start.toString({ timeZoneName: 'never' }),
						timeZone,
					},
					exclusiveEnd: {
						dateTime: range.end.toString({ timeZoneName: 'never' }),
						timeZone,
					},
				},
			});
		},
		timeZone,
	});

	if (!calendarIds) {
		throw Error('Missing calendar IDs');
	}

	const fromDate = Temporal.PlainDate.from(fromString).toZonedDateTime({ timeZone });
	const timeMin = fromDate.with({ hour: 0, minute: 0, second: 0, millisecond: 0 }).toInstant().toString();
	const timeMax = fromDate
		.add({ days: 7 })
		.with({ hour: 0, minute: 0, second: 0, millisecond: 0 })
		.toInstant()
		.toString();

	const calendarEventsQuery = useListCalendarEvents({
		calendarIds: calendarIds,
		includeEventsWithoutInterval: true,
		timeMin,
		timeMax,
	});

	function handleRemoveEventClick(id: string) {
		return removeCalendarEventMutation.mutateAsync({ id });
	}

	function handleCalendarEventUpdated(event: ICalendarEvent) {
		return createEventMutation.mutateAsync(event);
	}

	return {
		handleCalendarEventUpdated,
		handleRemoveEventClick,
		isLoading,
		selectInterval,
		timeZone,
		from: Temporal.Instant.from(timeMin).toZonedDateTimeISO(timeZone).toPlainDate(),
		to: Temporal.Instant.from(timeMax).toZonedDateTimeISO(timeZone).toPlainDate(),
		calendarEventsQuery,
		calendarQuery,
	};
}
